import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

const STYLE_ID = 'pf-styles';
const MAX_MSG_CLASS = 'pf-qty-max-msg';
const ERROR_TEXT_CLASS = 'pf-error-text';
const SHAKE_CLASS = 'pf-shake';

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes pf-shake {
      0%,100% { transform: translateX(0); }
      20%      { transform: translateX(-5px); }
      40%      { transform: translateX(5px); }
      60%      { transform: translateX(-3px); }
      80%      { transform: translateX(3px); }
    }
    .${SHAKE_CLASS} { animation: pf-shake 0.4s ease; }
    .${ERROR_TEXT_CLASS} {
      color: var(--color-error, #cc0000);
      font-size: var(--font-size--sm, 0.875rem);
      line-height: 1.4;
    }
    .${MAX_MSG_CLASS} {
      color: var(--color-error, #cc0000);
      font-size: var(--font-size--sm, 0.875rem);
      display: block;
      align-self: center;
    }
    .quantity-plus[disabled] {
      opacity: 0.35;
      cursor: not-allowed;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Read effective max from the freshly-fetched HTML document (server-rendered).
 * More reliable than parsing the variant JSON because Shopify's storefront
 * variant | json filter omits inventory_quantity and inventory_policy.
 * @param {Document|null|undefined} html
 * @returns {number|undefined}
 */
function maxFromHtml(html) {
  if (!html) return undefined;
  // Target the quantity input inside a product form (not a cart quantity selector)
  const input = html.querySelector('product-form-component quantity-selector-component input[name="quantity"]');
  if (!input) return undefined;
  const attr = input.getAttribute('max');
  if (attr === null || attr === '') return undefined;
  const parsed = parseInt(attr, 10);
  return isNaN(parsed) ? undefined : parsed;
}

class ProductFormComponent extends Component {
  /** @type {HTMLElement|null} */
  #maxMsgEl = null;
  #shakeTimer = null;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen on document — same pattern as sticky-add-to-cart.js.
    // Avoids the closest('.shopify-section') dependency and works regardless of DOM nesting.
    // #onVariantUpdate filters by productId so events from other products are ignored.
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyUpdate);
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    requestAnimationFrame(() => this.#ensureMaxMsg());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // ── Add-to-cart submit ────────────────────────────────────────────────────

  handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const submitButton = this.querySelector('[type="submit"][name="add"]');
    if (submitButton) submitButton.disabled = true;
    this.#hideError();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: new FormData(form),
      });

      const data = await response.json();

      if (!response.ok) {
        const genericError = this.dataset.errorGeneric || 'Đã xảy ra lỗi. Vui lòng thử lại.';
        const message =
          response.status === 422
            ? 'Sản phẩm này hiện không đủ số lượng trong kho, vui lòng kiểm tra lại giỏ hàng.'
            : data.description || data.message || genericError;

        this.#showError(message);
        this.dispatchEvent(
          new CartErrorEvent(this.dataset.sectionId, data.message, data.description, data.errors)
        );
        return;
      }

      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, parseInt(quantityInput?.value ?? '1', 10) || 1);

      this.dispatchEvent(
        new CartAddEvent(data, this.dataset.sectionId, {
          source: 'product-form-component',
          itemCount: quantity,
        })
      );

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = Theme.translations.added || '';
      }
    } catch (_) {
      // Network error — silently ignore
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  };

  // ── Variant events ────────────────────────────────────────────────────────

  /** Fires immediately on variant option click — clear stale UI before fetch completes. */
  #onVariantSelected = () => {
    this.#hideMaxMessage();
    this.#hideError();
  };

  /**
   * Fires after fetch + morph with the new variant's full JSON.
   *
   * WHY we read from event, not from DOM:
   * variant-picker only morphs <variant-picker>, NOT <product-form-component>.
   * So DOM attributes (input.max, input[name="id"].value) remain stale after switch.
   * event.detail.resource = selected_or_first_available_variant | json (always fresh).
   */
  #onVariantUpdate = (event) => {
    // Ignore events from other products (e.g. recommended products section)
    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && this.dataset.productId && eventProductId !== this.dataset.productId) return;

    const variant = event.detail?.resource;
    const qs = this.querySelector('quantity-selector-component');

    // ── 1. Sync hidden variant ID input (prevents submitting wrong variant) ──
    if (variant?.id && this.refs.variantId) {
      this.refs.variantId.value = String(variant.id);
    }

    if (!qs) {
      this.#hideMaxMessage();
      this.#hideError();
      return;
    }

    // ── 2. Get max from the server-rendered fetched HTML ─────────────────────
    // Shopify's variant | json omits inventory_quantity / inventory_policy, so we
    // cannot compute max from the variant object. The fetched HTML has the correct
    // server-side max already baked into the quantity input's max attribute.
    const newMax = maxFromHtml(event.detail?.data?.html);

    // Patch the input.max attribute so QuantitySelectorComponent stays in sync
    const input = qs.querySelector('input[name="quantity"]');
    if (input) {
      if (newMax !== undefined) {
        input.setAttribute('max', String(newMax));
      } else {
        input.removeAttribute('max');
      }
    }

    // ── 3. Re-run constraints (clamps quantity if over new max) ───────────────
    if (qs.updateConstraints && input) {
      const min = parseInt(input.getAttribute('min')) || 1;
      const step = parseInt(input.getAttribute('step')) || 1;
      qs.updateConstraints(min, newMax, step);
    }

    // ── 4. Evaluate max message for the (possibly clamped) quantity ───────────
    this.#evaluateMaxMessage(qs);
    this.#hideError();
  };

  // ── Quantity cap check ────────────────────────────────────────────────────

  #onQtyUpdate = () => {
    const qs = this.querySelector('quantity-selector-component');
    if (qs?.getCurrentValues) this.#evaluateMaxMessage(qs);
  };

  /** Show or hide the "max stock reached" message based on current value vs effectiveMax. */
  #evaluateMaxMessage(qs) {
    if (!qs?.getCurrentValues) return;
    const { value } = qs.getCurrentValues();
    const effectiveMax = qs.getEffectiveMax?.() ?? null;

    if (effectiveMax !== null && value >= effectiveMax) {
      const msg = qs.dataset.maxMsg || 'Đã đạt số lượng tối đa trong kho';
      this.#showMaxMessage(msg);
    } else {
      this.#hideMaxMessage();
    }
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────

  #ensureMaxMsg() {
    if (this.#maxMsgEl && !this.#maxMsgEl.isConnected) this.#maxMsgEl = null;
    if (this.#maxMsgEl) return;

    const wrapper = this.querySelector('.quantity-selector-wrapper');
    if (!wrapper) return;

    const span = document.createElement('span');
    span.className = `${MAX_MSG_CLASS} hidden`;
    span.setAttribute('aria-live', 'assertive');
    span.setAttribute('role', 'alert');
    wrapper.insertAdjacentElement('afterend', span);
    this.#maxMsgEl = span;
  }

  #showMaxMessage(text) {
    ensureStyles();
    if (!this.#maxMsgEl || !this.#maxMsgEl.isConnected) {
      this.#maxMsgEl = null;
      this.#ensureMaxMsg();
    }
    if (!this.#maxMsgEl) return;
    this.#maxMsgEl.textContent = text;
    this.#maxMsgEl.classList.remove('hidden');
  }

  #hideMaxMessage() {
    if (this.#maxMsgEl && !this.#maxMsgEl.isConnected) this.#maxMsgEl = null;
    this.#maxMsgEl?.classList.add('hidden');
  }

  #showError(message) {
    ensureStyles();

    const errorEl = this.refs.addToCartTextError;
    if (!errorEl) return;

    let textEl = errorEl.querySelector(`.${ERROR_TEXT_CLASS}`);
    if (!textEl) {
      textEl = document.createElement('span');
      textEl.className = ERROR_TEXT_CLASS;
      errorEl.appendChild(textEl);
    }
    textEl.textContent = message;
    errorEl.classList.remove('hidden');

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = message;
    }

    clearTimeout(this.#shakeTimer);
    errorEl.classList.remove(SHAKE_CLASS);
    void errorEl.offsetWidth;
    errorEl.classList.add(SHAKE_CLASS);
    this.#shakeTimer = setTimeout(() => errorEl.classList.remove(SHAKE_CLASS), 500);
  }

  #hideError() {
    const errorEl = this.refs.addToCartTextError;
    if (!errorEl) return;
    errorEl.classList.add('hidden');
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
  }
}

class AddToCartComponent extends Component {
  handleClick = () => {};
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}
